import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import InitialsBadge from '../../../components/ui/InitialsBadge';
import { getUserDisplayName, formatUserRole } from '../../../utils/avatarUtils';

const MemberCard = ({ member, onEditRole, onViewActivity, onRemoveMember, onMemberClick, canManageMembers, canRemoveMembers }) => {

  const getRoleColor = (role) => {
    const r = (role || '').toLowerCase();
    switch (r) {
      case 'owner':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'member':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "online":
      case "active":
        return "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]";
      case "away":
        return "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.5)]";
      case "offline":
      case "inactive":
        return "bg-slate-400";
      case "pending":
        return "bg-indigo-500";
      default:
        return "bg-slate-400";
    }
  };

  const formatLastActivity = (date, status = 'active') => {
    if (!date) return 'Never';
    const activityDate = new Date(date);
    if (isNaN(activityDate.getTime())) return 'Never';
    
    const now = new Date();
    const diffInMs = now - activityDate;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    const prefix = status === 'pending' ? 'Invited' : 'Active';

    if (diffInMinutes < 5 && status !== 'pending') return 'Online now';
    if (diffInMinutes < 60) return `${prefix} ${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${prefix} ${diffInHours}h ago`;
    if (diffInDays < 7) return `${prefix} ${diffInDays}d ago`;
    
    return `${prefix} on ${activityDate.toLocaleDateString()}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-ambient hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between min-w-0">
        <div 
          className="flex items-center space-x-3 min-w-0 flex-1 cursor-pointer group"
          onClick={() => onMemberClick && onMemberClick(member)}
        >
          <div className="relative shrink-0">
            {member.avatar && member.avatar !== '/assets/images/avatar.jpg' && !member.avatar.includes('placeholder') ? (
               <img 
                 src={member.avatar} 
                 alt={getUserDisplayName(member)} 
                 className="w-10 h-10 rounded-full object-cover border border-border"
                 onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
               />
            ) : (
                 <InitialsBadge name={getUserDisplayName(member)} size="md" />
            )}
            <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 ${getStatusColor(member.status)} rounded-full border-2 border-card`}></div>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-text-primary truncate text-sm group-hover:text-primary transition-colors">{getUserDisplayName(member)}</h3>
            <p className="text-xs text-text-secondary truncate">{member.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-0 ml-2 shrink-0">
          {canManageMembers && onEditRole && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEditRole(member)}
              className="h-7 w-7"
              disabled={member.status === 'pending'}
              title={member.status === 'pending' ? 'Cannot edit role for pending invitation' : 'Edit role'}
            >
              <Icon name="Edit" size={14} className={member.status === 'pending' ? 'opacity-50' : ''} />
            </Button>
          )}

          {canRemoveMembers && onRemoveMember && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRemoveMember(member)}
              className="h-7 w-7 text-destructive hover:text-destructive"
            >
              <Icon name="Trash2" size={14} />
            </Button>
          )}
        </div>
      </div>
      
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${getRoleColor(member.role)}`}>
          {formatUserRole(member.role || 'member')}
        </span>
        <span className="text-[10px] text-text-secondary font-medium">
          {formatLastActivity(member.lastActivity, member.status)}
        </span>
      </div>
    </div>
  );
};

export default MemberCard;